import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { EASE } from "../../lib/mrs";

const HERO_BG =
  "https://images.pexels.com/photos/8134745/pexels-photo-8134745.jpeg";

const HEADLINE = ["We bring nature", "into every", "design."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.6 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Modern luxury home surrounded by nature"
          className="w-full h-[115%] object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-[#0a0b0a]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0a] via-transparent to-[#0a0b0a]/40" />

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-20 pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.3 }}
          className="text-[11px] md:text-sm tracking-[0.28em] uppercase text-[#b89b72] mb-6 md:mb-8"
        >
          Where Vision Becomes Structure · Rooted in Nature
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display font-light text-[#eae7e0] leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight max-w-5xl"
        >
          {HEADLINE.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 1.4 }}
          className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-base md:text-lg text-[#9ca399] leading-relaxed">
            A piece of the outdoors, thoughtfully built into every space you
            live and work in, without exception.
          </p>
          <a
            href="#contact"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#b89b72] text-[#0a0b0a] text-[12px] tracking-[0.2em] uppercase hover:bg-[#cbb38e] transition-colors duration-300 self-start md:self-auto whitespace-nowrap"
          >
            Book Free Consultation
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 right-6 md:right-12 lg:right-20 z-10 hidden md:flex flex-col items-center gap-3"
      >
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#9ca399] [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="text-[#b89b72]"
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
